import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-hot-toast';
import {
  LuBanknote,
  LuCircleCheck,
  LuCopy,
  LuExternalLink,
  LuMapPin,
  LuPackageOpen,
  LuPaperclip,
  LuPencil,
  LuPhone,
  LuPlus,
  LuTrash2,
  LuTruck,
  LuUser,
} from 'react-icons/lu';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import IconButton from '../../components/ui/IconButton';
import { useTranslation } from '../../i18n/LanguageContext';
import ShipmentModal from './ShipmentModal';
import {
  SHIPMENT_STATUS,
  SHIPMENT_STATUS_OPTIONS,
  formatShipmentCost,
  formatShipmentDate,
  getCarrierLabel,
  getShipmentStatus,
  getTrackingUrl,
  summarizeShipments,
  todayInputValue,
} from '../../utils/shipments';
import { EmptyBlock, PANEL } from './visitModal/VisitUi';

const newId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

// Left accent of a card and the colour of its quick actions, per status tone
const CARD_ACCENTS = {
  neutral: 'tw-border-l-slate-300 dark:tw-border-l-white/20',
  info: 'tw-border-l-sky-400',
  success: 'tw-border-l-emerald-400',
  danger: 'tw-border-l-rose-400',
};
const QUICK_TONES = {
  neutral: 'tw-text-slate-600 hover:tw-bg-slate-500/10 dark:tw-text-slate-300',
  info: 'tw-text-sky-700 hover:tw-bg-sky-500/10 dark:tw-text-sky-300',
  success: 'tw-text-emerald-700 hover:tw-bg-emerald-500/10 dark:tw-text-emerald-300',
  danger: 'tw-text-rose-700 hover:tw-bg-rose-500/10 dark:tw-text-rose-300',
};

/**
 * Ziyaret modalindeki "Kargo Takip" sekmesi.
 * Kayıtlar Visits/{visitId}.shipments dizisinde tutulur; her değişiklik
 * anında yazılır (Stok sekmesiyle aynı davranış).
 */
const ShipmentTracking = ({
  visitId,
  shipments = [],
  onShipmentsChange,
  isReadOnly = false,
  currentUserName = '',
}) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`visitModal.shipments.${key}`, vars);
  const statusLabel = (status) => label(`status.${getShipmentStatus(status).value}`);
  const carrierName = (carrier) => (['kurye', 'diger'].includes(carrier) ? label(`carriers.${carrier}`) : getCarrierLabel(carrier));
  const [showModal, setShowModal] = useState(false);
  const [editingShipment, setEditingShipment] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // Silme onayı penceresi (eski tarayıcı onayı yerine)
  const [deletingShipment, setDeletingShipment] = useState(null);

  const summary = useMemo(() => summarizeShipments(shipments), [shipments]);

  // En yeni kayıt üstte: önce kargoya veriliş tarihi, yoksa oluşturulma zamanı
  const sortedShipments = useMemo(
    () =>
      [...shipments].sort((a, b) => {
        const aDate = new Date(a.shippedAt || a.createdAt || 0).getTime();
        const bDate = new Date(b.shippedAt || b.createdAt || 0).getTime();
        return bDate - aDate;
      }),
    [shipments]
  );

  const persist = async (nextShipments, successMessage) => {
    if (!visitId) {
      toast.error('Ziyaret bilgisi bulunamadı');
      throw new Error('missing visit id');
    }
    try {
      await updateDoc(doc(db, 'Visits', visitId), {
        shipments: nextShipments,
        lastUpdated: new Date().toISOString(),
      });
      onShipmentsChange(nextShipments);
      if (successMessage) toast.success(successMessage);
    } catch (error) {
      console.error('Error saving shipments:', error);
      toast.error('Kargo kaydı kaydedilirken bir hata oluştu');
      throw error;
    }
  };

  const handleSave = async (formData) => {
    if (editingShipment) {
      const next = shipments.map((item) =>
        item.id === editingShipment.id
          ? { ...item, ...formData, updatedAt: new Date().toISOString() }
          : item
      );
      await persist(next, 'Kargo kaydı güncellendi');
    } else {
      const record = {
        ...formData,
        id: newId(),
        createdAt: new Date().toISOString(),
        createdBy: currentUserName,
      };
      await persist([...shipments, record], 'Kargo kaydı eklendi');
    }
  };

  // Kart üzerindeki hızlı işlem: bir sonraki duruma geçir
  const handleQuickStatus = async (shipment, status) => {
    setBusyId(shipment.id);
    try {
      const next = shipments.map((item) => {
        if (item.id !== shipment.id) return item;
        const updated = { ...item, status, updatedAt: new Date().toISOString() };
        if (status !== SHIPMENT_STATUS.NOT_SENT && !updated.shippedAt) {
          updated.shippedAt = todayInputValue();
        }
        if (status === SHIPMENT_STATUS.DELIVERED && !updated.deliveredAt) {
          updated.deliveredAt = todayInputValue();
        }
        return updated;
      });
      await persist(next, `Durum "${getShipmentStatus(status).label}" olarak güncellendi`);
    } catch (error) {
      // persist zaten bildirdi
    } finally {
      setBusyId(null);
    }
  };

  // Onay penceresinde "Evet, sil" dendikten sonra çalışır
  const handleDelete = async (shipment) => {
    setBusyId(shipment.id);
    try {
      await persist(
        shipments.filter((item) => item.id !== shipment.id),
        'Kargo kaydı silindi'
      );
    } catch (error) {
      // persist zaten bildirdi
    } finally {
      setBusyId(null);
    }
  };

  const copyTracking = (trackingNumber) => {
    navigator.clipboard
      ?.writeText(trackingNumber)
      .then(() => toast.success('Takip numarası kopyalandı'))
      .catch(() => toast.error('Kopyalanamadı'));
  };

  const openNew = () => {
    setEditingShipment(null);
    setShowModal(true);
  };

  const openEdit = (shipment) => {
    setEditingShipment(shipment);
    setShowModal(true);
  };

  const summaryItems = [
    { key: 'total', value: summary.total, icon: LuPackageOpen, tone: 'tw-bg-slate-500/[0.08] tw-text-slate-700 dark:tw-text-slate-200' },
    { key: 'notSent', value: summary[SHIPMENT_STATUS.NOT_SENT], tone: 'tw-bg-slate-500/[0.08] tw-text-slate-600 dark:tw-text-slate-300' },
    { key: 'shipped', value: summary[SHIPMENT_STATUS.SHIPPED], icon: LuTruck, tone: 'tw-bg-sky-500/10 tw-text-sky-700 dark:tw-text-sky-300' },
    { key: 'delivered', value: summary[SHIPMENT_STATUS.DELIVERED], icon: LuCircleCheck, tone: 'tw-bg-emerald-500/10 tw-text-emerald-700 dark:tw-text-emerald-300' },
  ];

  return (
    <div className="tw-space-y-4">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          {summaryItems.map(({ key, value, icon: Icon, tone }) => (
            <span key={key} className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium ${tone}`}>
              {Icon && <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />}
              <strong className="tw-text-sm tw-font-bold tw-tabular-nums">{value}</strong>
              {label(`summary.${key}`)}
            </span>
          ))}
          {summary.totalCost > 0 && (
            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-amber-500/10 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-amber-800 dark:tw-text-amber-200">
              <LuBanknote aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
              <strong className="tw-text-sm tw-font-bold tw-tabular-nums">{formatShipmentCost(summary.totalCost)}</strong>
              {label('summary.cost')}
            </span>
          )}
        </div>

        <Button variant="primary" onClick={openNew} disabled={isReadOnly} className="max-sm:tw-flex-1">
          <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
          {label('add')}
        </Button>
      </div>

      {sortedShipments.length === 0 ? (
        <EmptyBlock
          icon={LuPackageOpen}
          title={label('emptyTitle')}
          text={label('emptyText')}
          action={!isReadOnly && (
            <Button onClick={openNew}>
              <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('firstRecord')}
            </Button>
          )}
        />
      ) : (
        <div className="tw-grid tw-grid-cols-1 tw-gap-3 xl:tw-grid-cols-2">
          {sortedShipments.map((shipment) => {
            const status = getShipmentStatus(shipment.status);
            const trackingUrl = getTrackingUrl(shipment.carrier, shipment.trackingNumber);
            const carrierLabel = carrierName(shipment.carrier);
            const shippedAt = formatShipmentDate(shipment.shippedAt);
            const deliveredAt = formatShipmentDate(shipment.deliveredAt);
            const cost = formatShipmentCost(shipment.cost);
            const attachments = shipment.attachments || [];
            const isBusy = busyId === shipment.id;

            return (
              <article
                key={shipment.id}
                className={`${PANEL} tw-flex tw-flex-col tw-gap-3 tw-border-l-4 tw-p-4 ${CARD_ACCENTS[status.tone]} ${isBusy ? 'tw-opacity-70' : ''}`}
              >
                <header className="tw-flex tw-items-start tw-gap-3">
                  <div className="tw-min-w-0 tw-flex-1 tw-space-y-1.5">
                    <Badge tone={status.tone}>{statusLabel(shipment.status)}</Badge>
                    <h4 className="tw-break-words tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{shipment.subject}</h4>
                  </div>
                  {!isReadOnly && (
                    <div className="tw-flex tw-shrink-0 tw-gap-1.5">
                      <IconButton label={label('edit')} onClick={() => openEdit(shipment)} disabled={isBusy}>
                        <LuPencil aria-hidden="true" className="tw-h-4 tw-w-4" />
                      </IconButton>
                      <IconButton label={label('delete')} tone="alert" onClick={() => setDeletingShipment(shipment)} disabled={isBusy}>
                        <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
                      </IconButton>
                    </div>
                  )}
                </header>

                {shipment.content && <p className="tw-whitespace-pre-line tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">{shipment.content}</p>}

                <ul className="tw-space-y-1.5 tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">
                  <li className="tw-flex tw-items-start tw-gap-2">
                    <LuUser aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
                    <span className="tw-min-w-0 tw-break-words">
                      {shipment.recipientName}
                      {shipment.recipientOrg ? ` · ${shipment.recipientOrg}` : ''}
                    </span>
                  </li>
                  {shipment.destination && (
                    <li className="tw-flex tw-items-start tw-gap-2">
                      <LuMapPin aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
                      <span className="tw-min-w-0 tw-break-words">{shipment.destination}</span>
                    </li>
                  )}
                  {shipment.recipientPhone && (
                    <li className="tw-flex tw-items-start tw-gap-2">
                      <LuPhone aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
                      <a href={`tel:${shipment.recipientPhone}`} className="tw-font-medium tw-text-teal-700 hover:tw-underline dark:tw-text-teal-300">
                        {shipment.recipientPhone}
                      </a>
                    </li>
                  )}
                  {(carrierLabel || shippedAt || deliveredAt) && (
                    <li className="tw-flex tw-items-start tw-gap-2">
                      <LuTruck aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
                      <span className="tw-min-w-0">
                        {[
                          carrierLabel,
                          shippedAt ? label('sentOn', { date: shippedAt }) : null,
                          deliveredAt ? label('deliveredOn', { date: deliveredAt }) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </li>
                  )}
                  {cost && (
                    <li className="tw-flex tw-items-start tw-gap-2">
                      <LuBanknote aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
                      <span className="tw-min-w-0">
                        {label('costLabel')} <strong className="tw-font-semibold tw-text-slate-900 dark:tw-text-white">{cost}</strong>
                      </span>
                    </li>
                  )}
                </ul>

                {shipment.trackingNumber && (
                  <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-2xl tw-bg-slate-500/[0.06] tw-py-1.5 tw-pl-3 tw-pr-1.5 dark:tw-bg-white/[0.04]">
                    <span className="tw-shrink-0 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400">{label('trackingNo')}</span>
                    <code className="tw-min-w-0 tw-flex-1 tw-truncate tw-font-mono tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{shipment.trackingNumber}</code>
                    <button
                      type="button"
                      onClick={() => copyTracking(shipment.trackingNumber)}
                      title={label('copy')}
                      aria-label={label('copy')}
                      className="tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-full tw-text-slate-500 tw-transition-colors hover:tw-bg-slate-500/10 hover:tw-text-slate-800 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-slate-300 dark:hover:tw-text-white"
                    >
                      <LuCopy aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                    </button>
                    {trackingUrl && (
                      <a
                        href={trackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={label('openCarrier', { carrier: carrierLabel })}
                        aria-label={label('openCarrier', { carrier: carrierLabel })}
                        className="tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-full tw-text-teal-700 tw-transition-colors hover:tw-bg-teal-500/10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-teal-300"
                      >
                        <LuExternalLink aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                      </a>
                    )}
                  </div>
                )}

                {attachments.length > 0 && (
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                    <LuPaperclip aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />
                    {attachments.map((file, index) => (
                      <a
                        key={file.storagePath || `${file.name}-${index}`}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={file.name}
                        className="tw-max-w-[12rem] tw-truncate tw-rounded-full tw-border tw-border-slate-200 tw-bg-white/80 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-sky-700 hover:tw-border-sky-400/60 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-sky-300"
                      >
                        {file.name}
                      </a>
                    ))}
                  </div>
                )}

                {shipment.note && (
                  <p className="tw-rounded-xl tw-bg-amber-400/10 tw-px-3 tw-py-2 tw-text-xs tw-leading-snug tw-text-amber-900 dark:tw-text-amber-100">{shipment.note}</p>
                )}

                {!isReadOnly && (
                  <footer className="tw-mt-auto tw-flex tw-flex-wrap tw-gap-1.5 tw-border-t tw-border-slate-200/70 tw-pt-3 dark:tw-border-white/10">
                    {SHIPMENT_STATUS_OPTIONS.filter(
                      (option) => option.value !== shipment.status
                    ).map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleQuickStatus(shipment, option.value)}
                        disabled={isBusy}
                        className={`tw-rounded-full tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 ${QUICK_TONES[option.tone]}`}
                      >
                        {label('markAs', { status: label(`status.${option.value}`) })}
                      </button>
                    ))}
                  </footer>
                )}
              </article>
            );
          })}
        </div>
      )}

      <ShipmentModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditingShipment(null);
        }}
        visitId={visitId}
        shipment={editingShipment}
        onSave={handleSave}
        defaultSender={currentUserName}
      />

      <ConfirmDialog
        isOpen={Boolean(deletingShipment)}
        tone="danger"
        icon={LuTrash2}
        title={label('confirmDelete.title')}
        message={label('confirmDelete.message', { subject: deletingShipment?.subject || '' })}
        confirmLabel={label('confirmDelete.confirm')}
        cancelLabel={t('visitModal.common.cancel')}
        busy={Boolean(deletingShipment) && busyId === deletingShipment.id}
        onConfirm={async () => {
          await handleDelete(deletingShipment);
          setDeletingShipment(null);
        }}
        onCancel={() => setDeletingShipment(null)}
      />
    </div>
  );
};

ShipmentTracking.propTypes = {
  visitId: PropTypes.string,
  shipments: PropTypes.array,
  onShipmentsChange: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  currentUserName: PropTypes.string,
};

export default ShipmentTracking;
